import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Table } from 'components/molecules/Table';
import { Pagination } from 'components/molecules/Pagination';
import { useAlert } from 'models/alertContext';
import { EPerPages, ISortOptions, mapToEPerPages } from 'models/table';
import { ActionTypes } from 'state/actions/alert';
import { Alert } from 'components/atoms/Alert';
import { Loader } from 'components/atoms/Loader';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { useTranslations } from 'hooks/useTranslations';
import { Box } from '@mui/material';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { useDictionary } from 'models/dictionaryContext';
import { IBatchesSearchForm } from 'models/licences';
import { CardList } from 'components/molecules/CardList';
import { formatDateToRequest } from 'utils/date';
import { objectToQueryString, prepareQueryValues } from 'helpers/searchQuery';
import { joinValuesToString } from 'helpers/joinValuesToString';
import { replaceUrlIfOnPage } from 'utils/history';
import { renderLicenceRowColor } from 'components/molecules/Table/helpers';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { useAccount } from 'pages/AccountPage/hooks';
import { useLicences } from '../../hooks';
import {
  batchesTableColumns,
  initialBatchesSearchForm,
  mapBatchesToDataSource,
} from '../../helpers';
import { BatchesSearchForm } from '../BatchesSearchForm';
import BatchTableCard from '../BatchTableCard';

const BATCH_DATE_FIELDS = ['requestDateFrom', 'requestDateTo'];

const BatchesPage = () => {
  const navigate = useNavigate();
  const { nextPageAlert, clearAlert } = useAlert();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();

  const initialFormValues = useMemo(() => {
    const queryValues = prepareQueryValues(
      initialBatchesSearchForm,
      new URLSearchParams(searchParams),
      BATCH_DATE_FIELDS,
    );
    return queryValues
      ? ({ ...initialBatchesSearchForm, ...queryValues } as IBatchesSearchForm)
      : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialBatchQuery = useMemo<Partial<IBatchesSearchForm>>(() => {
    const queryValues = prepareQueryValues(
      initialBatchesSearchForm,
      new URLSearchParams(searchParams),
    );
    return queryValues ?? {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [currentPage, setCurrentPage] = useState<number>(() =>
    Number.parseInt(searchParams.get('currentPage') || '1', 10),
  );
  const [perPage, setPerPage] = useState<EPerPages>(() =>
    mapToEPerPages(searchParams.get('perPage') || EPerPages.perPage25),
  );
  const [batchQuery, setBatchQuery] = useState<Partial<IBatchesSearchForm>>(
    () => initialBatchQuery,
  );
  const [sort, setSort] = useState<ISortOptions | null>(null);
  const [areDefaultsResolved, setAreDefaultsResolved] = useState<boolean>(false);
  const [isAccountLoaded, setIsAccountLoaded] = useState<boolean>(false);

  const buildBatchesQueryPath = (
    query: Partial<IBatchesSearchForm>,
    page: number,
    size: EPerPages,
  ): string => {
    const queryString = objectToQueryString({
      ...query,
      licenceAuthorityUniqueIds: joinValuesToString(query?.licenceAuthorityUniqueIds),
      statusUniqueIds: joinValuesToString(query?.statusUniqueIds),
      perPage: size,
      currentPage: page,
    });
    return `${pathname}?expand=batches&${queryString}`;
  };
  const [isSortLoaded, setIsSortLoaded] = useState<boolean>(true);
  const [isLoader, setLoader] = useState(true);
  const { isDesktop } = useThemeBreakpoint();
  const { t } = useTranslations();
  const { longDateFormat, getProperties } = useGlobalProperty();
  const scrollPosition = useRef(0);
  const {
    getBatchesSearchData,
    batches,
    batchesPaginator,
    weaponLicencingAuthority,
    getWeaponLicencingAuthority,
  } = useLicences();
  const { weaponLicencePrintBatchStatus, getWeaponLicencePrintBatchStatus } = useDictionary();
  const { account, getSelfAccountData } = useAccount();

  useEffect(() => {
    (async function loadSelfAccount() {
      await getSelfAccountData();
      setIsAccountLoaded(true);
    })();
  }, []);

  useEffect(() => {
    if (areDefaultsResolved) {
      return;
    }
    const hasUrlLicenceAuthority =
      Array.isArray(initialBatchQuery.licenceAuthorityUniqueIds) &&
      initialBatchQuery.licenceAuthorityUniqueIds.length > 0;
    if (hasUrlLicenceAuthority) {
      setAreDefaultsResolved(true);
      return;
    }
    if (!isAccountLoaded || !weaponLicencingAuthority) {
      return;
    }
    const userLicenceAuthorities = account
      ? weaponLicencingAuthority
          .filter((authority) =>
            account.licenceAuthorityIds.includes(authority.uniqueId.toString()),
          )
          .map((authority) => authority.uniqueId.toString())
      : [];
    if (userLicenceAuthorities.length) {
      setBatchQuery((prev) => ({
        ...prev,
        licenceAuthorityUniqueIds: userLicenceAuthorities,
      }));
    }
    setAreDefaultsResolved(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAccountLoaded, account, weaponLicencingAuthority, areDefaultsResolved]);

  useEffect(() => {
    if (!isLoader) clearAlert(ActionTypes.CLEAR_NEXT_PAGE_ALERT);
  }, [isLoader]);

  useEffect(() => {
    getProperties();
    if (!weaponLicencingAuthority) {
      getWeaponLicencingAuthority();
    }
    if (!weaponLicencePrintBatchStatus) {
      getWeaponLicencePrintBatchStatus();
    }
  }, []);

  useEffect(() => {
    if (!areDefaultsResolved) {
      return;
    }
    replaceUrlIfOnPage(buildBatchesQueryPath(batchQuery, currentPage, perPage));
    (async function getDataInit() {
      await getBatchesSearchData(currentPage, perPage, batchQuery, sort);
      setLoader(false);
      setIsSortLoaded(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, batchQuery, sort, areDefaultsResolved]);

  useEffect(() => {
    if (isSortLoaded) {
      window.scrollTo({ top: scrollPosition.current, behavior: 'auto' });
      scrollPosition.current = 0;
    }
  }, [isSortLoaded]);

  const dataSource = mapBatchesToDataSource(
    batches,
    longDateFormat,
    weaponLicencingAuthority,
    weaponLicencePrintBatchStatus,
  );

  const handleSetBatchQuery = (values: IBatchesSearchForm) => {
    setCurrentPage(1);
    setBatchQuery({
      ...values,
      requestDateFrom: values.requestDateFrom
        ? formatDateToRequest(values.requestDateFrom, true, true)
        : null,
      requestDateTo: values.requestDateTo
        ? formatDateToRequest(values.requestDateTo, true, true)
        : null,
    });
  };

  const onClickDetails = (uniqueId: string) => {
    if (!uniqueId) return;
    navigate(ERouteLinks.PrintoutBatchDetails.replace(':id', uniqueId));
  };

  return (
    <>
      {nextPageAlert && (
        <Box sx={{ mb: 2, mt: 2 }}>
          <Alert text={nextPageAlert.text} variant={nextPageAlert.variant} />
        </Box>
      )}

      <BatchesSearchForm
        onSubmit={handleSetBatchQuery}
        initialFormValues={initialFormValues ?? undefined}
      />

      {isLoader ? (
        <Loader isLoading={isLoader} />
      ) : (
        <div>
          {isDesktop ? (
            <Table
              columns={batchesTableColumns(t, onClickDetails)}
              dataSource={dataSource}
              onSort={(it) => {
                setSort(it);
                scrollPosition.current = window.scrollY;
              }}
              isSortLoaded={isSortLoaded}
              specificRowColor={renderLicenceRowColor}
              specificRowColorHover={renderLicenceRowColor}
              loading={isLoader}
            />
          ) : (
            <CardList
              items={dataSource}
              render={(data, index) => (
                <BatchTableCard key={index} data={data} onClickDetails={onClickDetails} />
              )}
            />
          )}
        </div>
      )}
      {batchesPaginator && (
        <Pagination
          count={batchesPaginator?.totalElements}
          onChangePage={(page) => setCurrentPage(page)}
          current={currentPage}
          perPage={perPage}
          onChangePerPage={(value) => setPerPage(value)}
          isLoading={false}
          isVisible={isSortLoaded}
        />
      )}
    </>
  );
};

export default BatchesPage;
